import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';
import React, { useEffect, useState } from 'react';

import CustomMetatag from '@/components/layouts/custom-metatag';
import Layout from '@/components/layouts/lcv';
import List, {
  NewsCategory,
  NewsListProps,
} from '@/components/sections/news-activities/list';
import { MENU_LCV_ABOUT_NEWS } from '@/contents/lcv/link';
import {
  getLCVNewsByCategory,
  getLCVNewsCategory,
  getLCVNewsHighlightCategory,
  getLCVNewsLatestByCategory,
} from '@/services/contentstack';
import clientAxios, { NEWS_PER_PAGE } from '@/services/contentstack/client';
import { IBody } from '@/types/services/contentstack';

const FooterLCVGroup = dynamic(
  () => import('@/components/layouts/lcv/footer-group'),
  {
    ssr: false,
  }
);
export async function getStaticPaths() {
  const res = await getLCVNewsCategory();

  const entries = res.data.entries;
  const paths = entries.map((entry) => ({
    params: { category: entry.url },
  }));

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const resNewsHighlightCategory = await getLCVNewsHighlightCategory(
    params.category
  );
  const resNewsLatestByCategory = await getLCVNewsLatestByCategory(
    params.category
  );

  if (!resNewsHighlightCategory?.data?.entries[0]) {
    return {
      redirect: {
        destination: `${MENU_LCV_ABOUT_NEWS.url}`,
      },
    };
  }

  let highlightEntry = [];
  let highlightId: string;
  if (
    resNewsHighlightCategory?.data?.entries?.[0]?.highlight &&
    resNewsHighlightCategory?.data?.entries?.[0]?.highlight.length > 0 &&
    resNewsHighlightCategory?.data?.entries?.[0]?.highlight?.[0]?.active
  ) {
    highlightEntry = resNewsHighlightCategory?.data?.entries?.[0]?.highlight;
    highlightId =
      resNewsHighlightCategory?.data?.entries?.[0]?.highlight?.[0]?.uid;
  } else if (
    resNewsLatestByCategory?.data?.entries &&
    resNewsLatestByCategory?.data?.entries.length > 0 &&
    resNewsLatestByCategory?.data?.entries[0].active
  ) {
    highlightEntry = resNewsLatestByCategory?.data?.entries;
    highlightId = resNewsLatestByCategory?.data?.entries?.[0]?.uid;
  }

  const highlightData = highlightEntry.map((o: any) => ({
    id: o?.uid,
    category: o?.category.map((c) => ({
      name: c?.name,
      url: c?.url,
    })),
    image_thumb: {
      url: o?.image_thumb?.url,
    },
    begin_date: o?.begin_date,
    name: o?.name,
    excerpt: o?.excerpt,
    url: o?.url,
  }));

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const res = await getLCVNewsByCategory(1, params.category, highlightId);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (highlightId !== 'undefined' && highlightId !== null) {
    res.data.entries = res.data.entries.map((o) => ({
      id: o?.uid,
      begin_date: o?.begin_date,
      name: o?.name,
      excerpt: o?.excerpt,
      image_thumb: {
        url: o?.image_thumb?.url,
      },
      category: o?.category.map((c) => ({
        name: c?.name,
        url: c?.url,
      })),
      url: o?.url,
    }));
  } else {
    res.data.entries = [];
  }

  const resCategory = await getLCVNewsCategory();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  resCategory.data.entries = resCategory.data.entries.map((o: any, index) => ({
    id: index + 1,
    name: o?.name,
    url: o?.url,
    metatag: {
      title: o?.metatag && o?.metatag?.title,
      description: o?.metatag && o?.metatag?.description,
      img: {
        url: o?.metatag?.img && o?.metatag?.img?.url,
      },
    },
    headline: {
      title: o?.headline && o?.headline?.headline_title,
      description: o?.headline && o?.headline?.headline_description,
    },
  }));

  const getCurrentCategory = (items, currentCategory) => {
    const filter_item = items.filter((item) => item.url === currentCategory);
    return filter_item.length ? filter_item[0] : null;
  };

  const category = getCurrentCategory(
    resCategory.data.entries,
    params.category
  );

  return {
    props: JSON.parse(
      JSON.stringify({
        ...res.data,
        category: resCategory.data,
        ...category,
        highlight: highlightData,
        currentCategory: params.category,
      })
    ),
  };
}

export default function Page(props) {
  const getItems = (items) => {
    if (!items || items.length === 0) return [];
    return items.map((o) => ({
      id: o?.id,
      category: {
        name: o?.category?.[0]?.name,
        url: o?.category?.[0]?.url,
      },
      beginDate: o?.begin_date,
      image: {
        src: o?.image_thumb?.url,
        alt: o?.name,
      },
      url: `${MENU_LCV_ABOUT_NEWS.url}/${o.category[0].url}/${o.url}`,
      title: o?.name,
      excerpt: o?.excerpt,
      button: {
        label: 'ดูรายละเอียด',
        icon: 'arrow-forward',
        url: `${MENU_LCV_ABOUT_NEWS.url}/${o.category[0].url}/${o.url}`,
      },
    }));
  };

  const getCategory = (
    items: NewsCategory[] = [],
    currentCategory: string
  ): NewsCategory[] => {
    return [
      {
        id: 0,
        name: 'ทั้งหมด',
        url: MENU_LCV_ABOUT_NEWS.url,
        active: false,
      },
      ...items.map((o) => ({
        id: o.id,
        name: o.name,
        url: `${MENU_LCV_ABOUT_NEWS.url}/${o.url}`,
        active: o.url == currentCategory,
      })),
    ] as NewsCategory[];
  };

  const [data, setData] = useState<NewsListProps>({
    title: 'ข่าวสารและกิจกรรม',
    description: `ติดตามข่าวสารและกิจกรรมต่างๆ ของอีซูซุได้ที่นี่`,
    items: getItems(props.entries),
    category: getCategory(props.category.entries, props.currentCategory),
    pagination: {
      current: 1,
      total: Math.ceil(props.count / NEWS_PER_PAGE),
    },
    notFound: `ขออภัย ยังไม่มีข่าวสารและกิจกรรมประเภทนี้`,
    highlight: getItems(props.highlight),
  });

  const updatePage = async (page) => {
    const dataLCVNewsByCategory: IBody = {
      route: 'getLCVNewsByCategory',
      category: props.currentCategory,
      page: page,
      id: props.highlight[0].id,
    };
    const res = await clientAxios({ data: dataLCVNewsByCategory });
    const _pagination = {
      current: page,
      total: Math.ceil(res.data.count / NEWS_PER_PAGE),
    };
    const _data = {
      ...data,
      items: getItems(res.data.entries),
      pagination: _pagination,
      highlight: getItems(props.highlight),
    };
    setData(_data);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const _data = {
      ...data,
      items: getItems(props.entries),
      category: getCategory(props.category.entries, props.currentCategory),
      pagination: {
        current: 1,
        total: Math.ceil(props.count / NEWS_PER_PAGE),
      },
      highlight: getItems(props.highlight),
    };
    setData(_data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.currentCategory,
    props.entries,
    props.category.entries,
    props.count,
    props.highlight,
  ]);

  return (
    <>
      <CustomMetatag
        title={props?.metatag?.title}
        description={props?.metatag?.description}
        imgURL={props?.metatag?.imgURL}
      />
      <main>
        <List {...data} onClickPage={updatePage} />
      </main>
      <FooterLCVGroup />
    </>
  );
}

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
